/**
 * slideshow-3d.js
 *
 * Non-invasive visual layer on top of Dawn's native <slideshow-component>.
 * Does NOT reimplement or fork Dawn's scroll/autoplay/counter logic (see assets/global.js,
 * class SlideshowComponent extends SliderComponent). This script only toggles CSS classes
 * so that assets/slideshow-3d.css can render a desktop "coverflow" depth effect.
 *
 * Active-slide detection uses the container's scroll position (scrollLeft), NOT an
 * IntersectionObserver on the transformed .slideshow__slide elements. An observer on
 * those elements creates a feedback loop: applying rotateY/translateZ changes each
 * slide's effective visual bounding box, which changes its reported intersection ratio,
 * which re-fires the observer, which re-applies transforms, forever — the page never
 * reaches an idle state. Reading scrollLeft avoids this entirely since child transforms
 * don't affect the parent's scroll position.
 *
 * Activation rules:
 *  - Only when viewport width >= 990px (desktop breakpoint used throughout this theme).
 *  - Only when the visitor has not requested reduced motion.
 *  - Re-evaluated on resize, so a browser resized across the breakpoint updates live.
 */
(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 990px)';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  function shouldActivate() {
    return (
      window.matchMedia(DESKTOP_QUERY).matches &&
      !window.matchMedia(REDUCED_MOTION_QUERY).matches
    );
  }

  function classifySlides(slides, activeIndex) {
    slides.forEach(function (slide, index) {
      slide.classList.remove('is-active', 'is-prev', 'is-next', 'is-far');
      var diff = index - activeIndex;
      if (diff === 0) {
        slide.classList.add('is-active');
      } else if (diff === -1) {
        slide.classList.add('is-prev');
      } else if (diff === 1) {
        slide.classList.add('is-next');
      } else {
        slide.classList.add('is-far');
      }
    });
  }

  function setupCoverflow(slideshowComponent) {
    var slidesContainer = slideshowComponent.querySelector('.slideshow');
    if (!slidesContainer) return;

    var slides = Array.prototype.slice.call(
      slidesContainer.querySelectorAll('.slideshow__slide')
    );
    if (slides.length === 0) return;

    var ticking = false;
    var lastIndex = -1;

    function updateActiveFromScroll() {
      ticking = false;
      var width = slidesContainer.clientWidth || 1;
      var rawIndex = Math.round(slidesContainer.scrollLeft / width);
      var activeIndex = Math.max(0, Math.min(slides.length - 1, rawIndex));
      if (activeIndex === lastIndex) return;
      lastIndex = activeIndex;
      classifySlides(slides, activeIndex);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateActiveFromScroll);
    }

    function activate() {
      if (slidesContainer.classList.contains('is-3d-coverflow')) return;
      slidesContainer.classList.add('is-3d-coverflow');
      lastIndex = -1;
      updateActiveFromScroll();
      slidesContainer.addEventListener('scroll', onScroll, { passive: true });
    }

    function deactivate() {
      if (!slidesContainer.classList.contains('is-3d-coverflow')) return;
      slidesContainer.classList.remove('is-3d-coverflow');
      slidesContainer.removeEventListener('scroll', onScroll);
      slides.forEach(function (slide) {
        slide.classList.remove('is-active', 'is-prev', 'is-next', 'is-far');
      });
    }

    function evaluate() {
      if (shouldActivate()) {
        activate();
      } else {
        deactivate();
      }
    }

    evaluate();
    window.addEventListener('resize', evaluate);
  }

  function init() {
    var slideshowComponents = document.querySelectorAll(
      'slideshow-component.slideshow-component--3d-enabled'
    );
    slideshowComponents.forEach(setupCoverflow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
